import React from "react";
import { observer } from "mobx-react";
import { TenantProfileCreateStore } from "../../store";
import { TenantProfileCreateForm } from "./TenantProfileCreateForm";
import { ErrorDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { PersonType } from "../../../../api/graphql";

export type TenantProfileCreateProps = {
  tenantProfileCreateStore: TenantProfileCreateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const TenantProfileCreate: React.FC<TenantProfileCreateProps> = observer(
  ({ tenantProfileCreateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={tenantProfileCreateStore.isSaving} />
        <TenantProfileCreateForm
          tenantProfileCreateStore={tenantProfileCreateStore}
          personTypeError={tenantProfileCreateStore.errors.personType?.message}
          personTypeOptions={tenantProfileCreateStore.getPersonTypeOptions()}
          onPersonTypeChange={(value: PersonType | "") => {
            tenantProfileCreateStore.setPersonTypeValue(value);
          }}
          personType={tenantProfileCreateStore.entityValues.personType}
          legalNameError={tenantProfileCreateStore.errors.legalName?.message}
          onLegalNameChange={(value: string) => {
            tenantProfileCreateStore.setLegalNameValue(value);
          }}
          legalName={tenantProfileCreateStore.entityValues.legalName}
          ownPublisherError={
            tenantProfileCreateStore.errors.ownPublisher?.message
          }
          onOwnPublisherChange={(value) => {
            tenantProfileCreateStore.setOwnPublisherValue(value);
          }}
          ownPublisherSelectedValue={
            tenantProfileCreateStore.entityValues.showOwnPublisher
          }
          onOwnPublisherSelected={(value) => {
            tenantProfileCreateStore.setSelectedOwnPublisher(value);
          }}
          ownPublisherOptions={tenantProfileCreateStore.getOwnPublisherOptions()}
          businessIdError={tenantProfileCreateStore.errors.businessId?.message}
          onBusinessIdChange={(value: string) => {
            tenantProfileCreateStore.setBusinessIdValue(value);
          }}
          businessId={tenantProfileCreateStore.entityValues.businessId}
          legalEmailError={tenantProfileCreateStore.errors.legalEmail?.message}
          onLegalEmailChange={(value: string) => {
            tenantProfileCreateStore.setLegalEmailValue(value);
          }}
          legalEmail={tenantProfileCreateStore.entityValues.legalEmail}
          streetAndNumberError={
            tenantProfileCreateStore.errors.streetAndNumber?.message
          }
          onStreetAndNumberChange={(value: string) => {
            tenantProfileCreateStore.setStreetAndNumberValue(value);
          }}
          streetAndNumber={
            tenantProfileCreateStore.entityValues.streetAndNumber
          }
          floorError={tenantProfileCreateStore.errors.floor?.message}
          onFloorChange={(value: string) => {
            tenantProfileCreateStore.setFloorValue(value);
          }}
          floor={tenantProfileCreateStore.entityValues.floor}
          suiteError={tenantProfileCreateStore.errors.suite?.message}
          onSuiteChange={(value: string) => {
            tenantProfileCreateStore.setSuiteValue(value);
          }}
          suite={tenantProfileCreateStore.entityValues.suite}
          postalCodeError={tenantProfileCreateStore.errors.postalCode?.message}
          onPostalCodeChange={(value: string) => {
            tenantProfileCreateStore.setPostalCodeValue(value);
          }}
          postalCode={tenantProfileCreateStore.entityValues.postalCode}
          countryError={tenantProfileCreateStore.errors.country?.message}
          onCountryChange={(value) => {
            tenantProfileCreateStore.setCountryValue(value);
          }}
          countrySelectedValue={
            tenantProfileCreateStore.entityValues.showCountry
          }
          onCountrySelected={(value) => {
            tenantProfileCreateStore.setSelectedCountry(value);
          }}
          countryOptions={tenantProfileCreateStore.getCountryOptions()}
          provinceError={tenantProfileCreateStore.errors.province?.message}
          onProvinceChange={(value) => {
            tenantProfileCreateStore.setProvinceValue(value);
          }}
          provinceSelectedValue={
            tenantProfileCreateStore.entityValues.showProvince
          }
          onProvinceSelected={(value) => {
            tenantProfileCreateStore.setSelectedProvince(value);
          }}
          provinceOptions={tenantProfileCreateStore.getProvinceOptions()}
          cityError={tenantProfileCreateStore.errors.city?.message}
          onCityChange={(value: string) => {
            tenantProfileCreateStore.setCityValue(value);
          }}
          city={tenantProfileCreateStore.entityValues.city}
          vatStatusError={tenantProfileCreateStore.errors.vatStatus?.message}
          onVatStatusChange={(value) => {
            tenantProfileCreateStore.setVatStatusValue(value);
          }}
          vatStatusSelectedValue={
            tenantProfileCreateStore.entityValues.showVatStatus
          }
          onVatStatusSelected={(value) => {
            tenantProfileCreateStore.setSelectedVatStatus(value);
          }}
          vatStatusOptions={tenantProfileCreateStore.getVatStatusOptions()}
          taxIdError={tenantProfileCreateStore.errors.taxId?.message}
          onTaxIdChange={(value: string) => {
            tenantProfileCreateStore.setTaxIdValue(value);
          }}
          taxId={tenantProfileCreateStore.entityValues.taxId}
          grossReceiptsStatusError={
            tenantProfileCreateStore.errors.grossReceiptsStatus?.message
          }
          onGrossReceiptsStatusChange={(value) => {
            tenantProfileCreateStore.setGrossReceiptsStatusValue(value);
          }}
          grossReceiptsStatusSelectedValue={
            tenantProfileCreateStore.entityValues.showGrossReceiptsStatus
          }
          onGrossReceiptsStatusSelected={(value) => {
            tenantProfileCreateStore.setSelectedGrossReceiptsStatus(value);
          }}
          grossReceiptsStatusOptions={tenantProfileCreateStore.getGrossReceiptsStatusOptions()}
          grossReceiptsIdError={
            tenantProfileCreateStore.errors.grossReceiptsId?.message
          }
          onGrossReceiptsIdChange={(value: string) => {
            tenantProfileCreateStore.setGrossReceiptsIdValue(value);
          }}
          grossReceiptsId={
            tenantProfileCreateStore.entityValues.grossReceiptsId
          }
          billingEmailError={
            tenantProfileCreateStore.errors.billingEmail?.message
          }
          onBillingEmailChange={(value: string) => {
            tenantProfileCreateStore.setBillingEmailValue(value);
          }}
          billingEmail={tenantProfileCreateStore.entityValues.billingEmail}
          billingCurrencyError={
            tenantProfileCreateStore.errors.billingCurrency?.message
          }
          onBillingCurrencyChange={(value) => {
            tenantProfileCreateStore.setBillingCurrencyValue(value);
          }}
          billingCurrencySelectedValue={
            tenantProfileCreateStore.entityValues.showBillingCurrency
          }
          onBillingCurrencySelected={(value) => {
            tenantProfileCreateStore.setSelectedBillingCurrency(value);
          }}
          billingCurrencyOptions={tenantProfileCreateStore.getBillingCurrencyOptions()}
          printerNameError={
            tenantProfileCreateStore.errors.printerName?.message
          }
          onPrinterNameChange={(value: string) => {
            tenantProfileCreateStore.setPrinterNameValue(value);
          }}
          printerName={tenantProfileCreateStore.entityValues.printerName}
          printerStreetAndNumberError={
            tenantProfileCreateStore.errors.printerStreetAndNumber?.message
          }
          onPrinterStreetAndNumberChange={(value: string) => {
            tenantProfileCreateStore.setPrinterStreetAndNumberValue(value);
          }}
          printerStreetAndNumber={
            tenantProfileCreateStore.entityValues.printerStreetAndNumber
          }
          printerFloorError={
            tenantProfileCreateStore.errors.printerFloor?.message
          }
          onPrinterFloorChange={(value: string) => {
            tenantProfileCreateStore.setPrinterFloorValue(value);
          }}
          printerFloor={tenantProfileCreateStore.entityValues.printerFloor}
          printerSuiteError={
            tenantProfileCreateStore.errors.printerSuite?.message
          }
          onPrinterSuiteChange={(value: string) => {
            tenantProfileCreateStore.setPrinterSuiteValue(value);
          }}
          printerSuite={tenantProfileCreateStore.entityValues.printerSuite}
          printerPostalCodeError={
            tenantProfileCreateStore.errors.printerPostalCode?.message
          }
          onPrinterPostalCodeChange={(value: string) => {
            tenantProfileCreateStore.setPrinterPostalCodeValue(value);
          }}
          printerPostalCode={
            tenantProfileCreateStore.entityValues.printerPostalCode
          }
          printerCountryError={
            tenantProfileCreateStore.errors.printerCountry?.message
          }
          onPrinterCountryChange={(value) => {
            tenantProfileCreateStore.setPrinterCountryValue(value);
          }}
          printerCountrySelectedValue={
            tenantProfileCreateStore.entityValues.showPrinterCountry
          }
          onPrinterCountrySelected={(value) => {
            tenantProfileCreateStore.setSelectedPrinterCountry(value);
          }}
          printerCountryOptions={tenantProfileCreateStore.getPrinterCountryOptions()}
          printerProvinceError={
            tenantProfileCreateStore.errors.printerProvince?.message
          }
          onPrinterProvinceChange={(value) => {
            tenantProfileCreateStore.setPrinterProvinceValue(value);
          }}
          printerProvinceSelectedValue={
            tenantProfileCreateStore.entityValues.showPrinterProvince
          }
          onPrinterProvinceSelected={(value) => {
            tenantProfileCreateStore.setSelectedPrinterProvince(value);
          }}
          printerProvinceOptions={tenantProfileCreateStore.getPrinterProvinceOptions()}
          printerCityError={
            tenantProfileCreateStore.errors.printerCity?.message
          }
          onPrinterCityChange={(value: string) => {
            tenantProfileCreateStore.setPrinterCityValue(value);
          }}
          printerCity={tenantProfileCreateStore.entityValues.printerCity}
          sadaicFtpUrlError={
            tenantProfileCreateStore.errors.sadaicFtpUrl?.message
          }
          onSadaicFtpUrlChange={(value: string) => {
            tenantProfileCreateStore.setSadaicFtpUrlValue(value);
          }}
          sadaicFtpUrl={tenantProfileCreateStore.entityValues.sadaicFtpUrl}
          sadaicFtpUserError={
            tenantProfileCreateStore.errors.sadaicFtpUser?.message
          }
          onSadaicFtpUserChange={(value: string) => {
            tenantProfileCreateStore.setSadaicFtpUserValue(value);
          }}
          sadaicFtpUser={tenantProfileCreateStore.entityValues.sadaicFtpUser}
          sadaicFtpPasswordError={
            tenantProfileCreateStore.errors.sadaicFtpPassword?.message
          }
          onSadaicFtpPasswordChange={(value: string) => {
            tenantProfileCreateStore.setSadaicFtpPasswordValue(value);
          }}
          sadaicFtpPassword={
            tenantProfileCreateStore.entityValues.sadaicFtpPassword
          }
          sadaicEmailError={
            tenantProfileCreateStore.errors.sadaicEmail?.message
          }
          onSadaicEmailChange={(value: string) => {
            tenantProfileCreateStore.setSadaicEmailValue(value);
          }}
          sadaicEmail={tenantProfileCreateStore.entityValues.sadaicEmail}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={tenantProfileCreateStore.isProcessing}
          onSave={() => tenantProfileCreateStore.saveNewTenantProfile()}
          onCancel={() => tenantProfileCreateStore.cancelNewTenantProfile()}
        />
        <ErrorDialog
          open={tenantProfileCreateStore.errorDialogOpened}
          title={tenantProfileCreateStore.errorTitle}
          description={tenantProfileCreateStore.errorDescription}
          onClose={() => tenantProfileCreateStore.closeErrorDialog()}
        />
      </>
    );
  }
);
